import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ChangeDetectorRef
} from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { distinctUntilChanged, map, withLatestFrom } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ComponentStatus } from '../types';

export type DateInputStatus = ComponentStatus<'pattern' | 'minDate' | 'existingDate'>;

@Component({
    selector: 'fcp-date-input',
    templateUrl: './fcp-date-input.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FcpDateInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input() placeholder = '';
    @Input() value = '';
    @Input() e2eHook = '';
    @Input() disabled = false;
    @Input() minDate = new Date();
    @Input() errorMessages: DateErrorMessages = { minDate: '', existingDate: '', pattern: '' };
    @Input() compact = false;

    @Output() blurChange = new EventEmitter<{ value: string; status: DateInputStatus }>();

    public inputForm: FormControl & { value: string };
    public dateinputStatus: DateInputStatus;

    private formChangesSubscription: Subscription;
    private readonly separatorChar = '/';

    constructor(private cdr: ChangeDetectorRef) {
        const getISOStringDateFromInput = (inputValue: string) => {
            if (!inputValue) {
                return '';
            }
            const [day, month, year] = inputValue.split(this.separatorChar);
            const dateISOString = `${year}-${month}-${day}`;

            return Number.isNaN(new Date(dateISOString).getTime()) ? '' : dateISOString;
        };

        const validFormatValidator = Validators.pattern(
            `\\d{2}[\\${this.separatorChar}]\\d{2}[\\${this.separatorChar}]\\d{4}`
        );
        const existingDateValidator: ValidatorFn = (control: AbstractControl) => {
            if (!control.value) {
                return null;
            }

            const ISOStringDateInput = getISOStringDateFromInput(control.value);
            if (!ISOStringDateInput) {
                return { existingDate: true };
            }

            const isUnexistingDate = ISOStringDateInput !== new Date(ISOStringDateInput).toISOString().slice(0, 10);
            if (isUnexistingDate) {
                return { existingDate: true };
            }

            return null;
        };
        const minDateValidator: ValidatorFn = (control: AbstractControl) => {
            if (!control.value) {
                return null;
            }

            const ISOStringDateInput = getISOStringDateFromInput(control.value);
            if (!ISOStringDateInput) {
                return null; // Don't validate minDate if the date is incomplete/invalid
            }

            return new Date(ISOStringDateInput) < this.minDate ? { minDate: true } : null;
        };

        this.inputForm = new FormControl('', [validFormatValidator, existingDateValidator, minDateValidator]);
    }

    ngOnInit() {
        const valueChanges$ = this.inputForm.valueChanges.pipe(distinctUntilChanged());
        this.formChangesSubscription = this.inputForm.statusChanges
            .pipe(
                withLatestFrom(valueChanges$),
                map(([status, value]: [string, string]) => [this.getDateInputStatus(this.inputForm), value])
            )
            .subscribe(() => {
                this.cdr.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        const newValueReceived = changes.value && this.inputForm.value !== this.value;
        if (newValueReceived) {
            this.inputForm.setValue(changes.value.currentValue);
            this.cdr.markForCheck();
        }

        if (changes.minDate) {
            this.inputForm.updateValueAndValidity();
            this.cdr.markForCheck();
        }

        if (changes.disabled) {
            if (this.disabled) {
                this.inputForm.disable();
            }
            if (!this.disabled && this.inputForm.disabled) {
                this.inputForm.enable();
            }
            this.cdr.markForCheck();
        }
    }

    ngOnDestroy() {
        this.formChangesSubscription.unsubscribe();
    }

    public maskValue(value, keyPressedEv: KeyboardEvent) {
        if (this.isNotDigitValue(keyPressedEv.key)) {
            keyPressedEv.preventDefault();
        }

        const inputLength = value.length;
        const isDayTyped = inputLength === 2;
        const isMonthTyped = inputLength === 5;
        if (isDayTyped || isMonthTyped) {
            this.inputForm.setValue((value += this.separatorChar));
            this.cdr.markForCheck();
        }
    }

    public onBlur(inputForm: FormControl & { value: string }) {
        this.blurChange.emit({
            value: inputForm.value,
            status: this.dateinputStatus
        });
        this.cdr.markForCheck();
    }

    private getDateInputStatus(inputForm: FormControl) {
        this.dateinputStatus = {
            isInvalid: inputForm.invalid,
            isValid: inputForm.valid,
            isDisabled: inputForm.disabled,
            isPending: inputForm.pending,
            errors: inputForm.errors || {}
        };
        this.cdr.markForCheck();
        return this.dateinputStatus;
    }

    private isNotDigitValue(keyValue: string) {
        return !'0123456789'.includes(keyValue);
    }
}

interface DateErrorMessages {
    minDate: string;
    existingDate: string;
    pattern: string;
}
